import React, { forwardRef } from 'react'
import Select, { OptionProps, ValueContainerProps, components } from 'react-select'
import styled from 'styled-components'
import { styleTheme } from '../../../style/theme'
import { CheckBox } from '../../ui-elements/CheckBox'

import { SingleSelectPropsType, MultiSelectPropsType, MultiCheckBoxSelectPropsType } from './SelectType'
import { useConfig } from 'customer/routes'
import { getPrimaryColor } from 'customer/style/utils'

export const SingleSelect = forwardRef<any, SingleSelectPropsType>(({ ...props }, ref) => (
  <StyledComponent
    className={props.className}
    classNamePrefix="react-select"
    isMulti={false}
    isClearable={props.isClearable}
    ref={ref}
    isDisabled={props.disabled}
    isSearchable={props.isSearchable || true}
    {...props}
  />
))

export const MultiSelect = forwardRef<any, MultiSelectPropsType>(({ ...props }, ref) => (
  <StyledComponent
    className={props.className}
    classNamePrefix="react-select"
    isMulti
    closeMenuOnSelect={false}
    noOptionsMessage={() => '全て選択済みです。'}
    ref={ref}
    isDisabled={props.disabled}
    {...props}
  />
))

// NOTE: MultiCheckBoxSelectの内部で定義した場合、CheckBoxをクリックした際に際レンダリングされる
// そのため、プルダウンの外部でクリックしても閉じれないため、ここで定義する必要がある
const CustomValueContainer = (props: ValueContainerProps) => {
  const { children, getValue, selectProps } = props
  const selectedCount = getValue().length
  const label = selectProps['aria-label']

  return (
    <components.ValueContainer {...props}>
      {props.hasValue ? (
        <div className="react-select-value">
          {label}({selectedCount})
        </div>
      ) : null}

      {React.Children.map(children, (child) => {
        return React.isValidElement(child) && child.type !== components.Placeholder ? child : null
      })}
    </components.ValueContainer>
  )
}

export const MultiCheckBoxSelect = forwardRef<any, MultiCheckBoxSelectPropsType>(({ ...props }, ref) => {
  const { primaryColor } = useConfig() as { primaryColor?: string }

  const customStyles = {
    multiValue: () => ({
      display: 'none',
    }),
  }

  const Placeholder = (props) => {
    const { children } = props
    return (
      <components.Placeholder {...props}>
        <div className="react-select-placeholder">{children}(0)</div>
      </components.Placeholder>
    )
  }

  const Option = (props: OptionProps<any>) => {
    return (
      <components.SelectContainer {...props}>
        <div className="react-select-checkbox-wrapper">
          <CheckBox
            className={`react-select-checkbox ${props.options.length}`}
            id={props.data.value.toString()}
            label={props.data.label}
            onChange={(e) => {
              e.stopPropagation()
              props.selectOption(props.data)
            }}
            checked={props.isSelected}
            checkStyle="fill"
            primaryColor={primaryColor}
          />
        </div>
      </components.SelectContainer>
    )
  }

  return (
    <StyledComponent
      aria-label={props.label}
      className={props.className}
      classNamePrefix="react-select"
      isMulti
      closeMenuOnSelect={false}
      ref={ref}
      isClearable={false}
      isDisabled={props.disabled}
      hideSelectedOptions={false}
      isSearchable={false}
      components={{
        Option,
        Placeholder,
        ValueContainer: CustomValueContainer,
      }}
      styles={customStyles}
      primaryColor={primaryColor}
      {...props}
    />
  )
})

const StyledComponent = styled(Select)`
  width: 100%;

  & .react-select__menu-list {
    padding: 10px 0px;
  }

  & .react-select__control {
    border: 1px solid ${styleTheme.colors.gray.base};
    border-radius: 6px;
    cursor: pointer;
    padding: 8px;

    &:hover {
      border: 1px solid ${getPrimaryColor};
    }

    &--is-focused {
      box-shadow: 0 0 0 1px ${getPrimaryColor};
      border: 1px solid ${getPrimaryColor};
    }
  }

  & .react-select__clear-indicator {
    margin: 0 4px 0 0;
    position: relative;

    > svg {
      display: none;
    }

    &::before,
    &::after {
      background: ${styleTheme.colors.black};
      border-radius: 2px;
      content: '';
      height: 16px;
      left: 50%;
      position: absolute;
      top: 50%;
      transition: 0.2s;
      width: 3px;
    }

    &::before {
      transform: translate(-50%, -50%) rotate(45deg);
    }

    &::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }

    &:hover {
      &::before,
      &::after {
        background: ${styleTheme.colors.teal.base};
      }
    }
  }

  & .react-select__dropdown-indicator {
    position: relative;

    > svg {
      display: none;
    }

    &::after {
      content: '';
      width: 8px;
      height: 8px;
      border-left: 2px solid ${styleTheme.colors.black};
      border-bottom: 2px solid ${styleTheme.colors.black};
      transform: rotate(-45deg) translate(1px, -1px);
    }
  }

  & .react-select__indicator-separator {
    display: none;
  }

  & .react-select__placeholder {
    color: ${styleTheme.colors.gray.base};
  }

  & .react-select__option {
    cursor: pointer;

    &:active {
      background: ${styleTheme.colors.teal.light};
    }

    &--is-focused {
      background: ${styleTheme.colors.teal.light};
    }

    &--is-selected {
      background: ${styleTheme.colors.teal.base};
      color: ${styleTheme.colors.white};
    }
  }

  & .react-select__multi-value {
    border-radius: 12px;

    &__label {
      background: ${styleTheme.colors.teal.light};
      color: ${styleTheme.colors.black};
      font-size: 14px;
      padding: 4px 16px;
    }

    &__remove {
      background: ${styleTheme.colors.teal.light};
      color: ${styleTheme.colors.black};

      &:hover {
        background: ${styleTheme.colors.teal.light};
        color: ${styleTheme.colors.teal.base};
      }
    }
  }

  .react-select-placeholder {
    color: #363636;
  }

  .react-select-value {
    color: #363636;
    margin-left: 2px;
  }

  .react-select-checkbox-wrapper {
    font-size: 14px;
    padding: 6px 24px;

    .react-select-checkbox {
      width: 100%;
      align-items: center;
      color: ${styleTheme.colors.black};
    }
  }
`
